import React from 'react'
import { ShoppingCart, Briefcase, Building2, User, Shield } from 'lucide-react'

export interface Tab {
  id: string
  label: string
  icon: React.ComponentType<{ className?: string }>
  description: string
}

interface SettingsTabsProps {
  activeTab: string
  onTabChange: (tabId: string) => void
  tabs: Tab[]
}

/**
 * SettingsTabs Component
 * Provides accessible tab navigation for admin settings
 * Follows ARIA tab pattern for accessibility
 */
export const SettingsTabs: React.FC<SettingsTabsProps> = ({ activeTab, onTabChange, tabs }) => {
  return (
    <div className="border-b border-gray-200 mb-6 sm:mb-8">
      <nav
        className="flex space-x-2 sm:space-x-4 lg:space-x-8 overflow-x-auto pb-1 scrollbar-hide"
        role="tablist"
        aria-label="Settings tabs"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {tabs.map((tab) => {
          const Icon = tab.icon
          const isActive = activeTab === tab.id

          return (
            <button
              key={tab.id}
              role="tab"
              aria-selected={isActive}
              aria-controls={`${tab.id}-panel`}
              id={`${tab.id}-tab`}
              onClick={() => onTabChange(tab.id)}
              className={`
                flex items-center space-x-2 sm:space-x-3 py-3 sm:py-4 px-2 sm:px-3 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap min-w-0 flex-shrink-0
                transition-colors duration-200 touch-manipulation
                ${isActive
                  ? 'border-blue-500 text-blue-600 bg-blue-50'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                }
                focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-t-md
                active:scale-95 transform transition-transform
              `}
            >
              <Icon className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
              <div className="text-left min-w-0">
                <div className="font-semibold truncate">{tab.label}</div>
                <div className="text-xs opacity-75 font-normal hidden sm:block">{tab.description}</div>
              </div>
            </button>
          )
        })}
      </nav>
    </div>
  )
}

// Default tabs configuration
export const DEFAULT_TABS: Tab[] = [
  {
    id: 'marketplace',
    label: 'Marketplace',
    icon: ShoppingCart,
    description: 'Products, vendors, and catalog settings'
  },
  {
    id: 'enterprise',
    label: 'Enterprise Quotation System',
    icon: Briefcase,
    description: 'RFQs, quotes, and ROI simulator'
  },
  {
    id: 'government',
    label: 'Government Contracting',
    icon: Building2,
    description: 'Procurement, compliance, and bidding'
  },
  {
    id: 'account',
    label: 'Account Settings',
    icon: User,
    description: 'User management and permissions'
  },
  {
    id: 'captcha',
    label: 'CAPTCHA Statistics',
    icon: Shield,
    description: 'Bot protection metrics and monitoring'
  }
]

